import { Injectable } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { hatsAppConsumerService } from 'src/app/api/proxy/whatsapp/services/hats-app-consumer.service';
import { OptInRequest } from 'src/app/api/proxy/whatsapp/whatsapp-models';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { SecureStorageService } from '../storage/secure-storage.service';
import { EnvironmentConfigService } from '../environment-config-service/environment-config.service';
import { MyProfileService } from '../my-profile-service/my-profile.service';
import { BaseApiService } from '../base-api-service/base-api.service';
import { PromptService } from '../promtp-service/prompt.service';
import { differenceInCalendarDays, formatISO, parseISO } from 'date-fns';

/** After this many calendar days from first prompt, treat as "Later" and hide the banner. */
const WHATSAPP_OPT_IN_PROMPT_MAX_CALENDAR_DAYS = 14;

const WA_ME_BASE = 'https://wa.me/';
const OPT_IN_PREFIX = 'OptIn:';

/** Optional browser adapter for opening URLs; used for testing. Production uses @capacitor/browser. */
export interface WhatsAppBrowserAdapter {
  open(options: { url: string }): Promise<void>;
}

@Injectable({
  providedIn: 'root',
})
export class WhatsAppService extends BaseApiService {

  private readonly NETWORK_TIMEOUT = 25000;

  constructor(
    protected override _promptService: PromptService,
    private consumerService: hatsAppConsumerService,
    private envConfig: EnvironmentConfigService,
    private myProfileService: MyProfileService,
  ) {
    super(_promptService);
  }

  /** Set in unit tests to capture opened URLs; production leaves this undefined. */
  setTestBrowser(adapter: WhatsAppBrowserAdapter | null): void {
    this._testBrowserAdapter = adapter;
  }
  private _testBrowserAdapter: WhatsAppBrowserAdapter | null | undefined;

  /**
   * Opens WhatsApp (or WhatsApp Web) with an optional pre-filled message and optional phone number.
   * Works on web, iOS, and Android. Uses wa.me URLs; on native, opens the app when installed.
   * @param text Pre-filled message for the user to send (e.g. opt-in text).
   * @param phoneNumber Optional international number (digits only after normalization). If empty after normalization, opens without a number.
   */
  async openWithPreFilledMessage(text: string, phoneNumber?: string): Promise<void> {
    const encodedText = encodeURIComponent(text);
    let path = '';
    if (phoneNumber != null && phoneNumber !== '') {
      const digits = this.normalizePhoneNumber(phoneNumber);
      path = digits.length > 0 ? `${digits}?text=${encodedText}` : `?text=${encodedText}`;
    } else {
      path = `?text=${encodedText}`;
    }
    const url = `${WA_ME_BASE}${path}`;
    const opener = this._testBrowserAdapter ?? Browser;
    await opener.open({ url });
  }

  /**
   * Requests a one-time opt-in code from the WhatsApp API, then opens WhatsApp with the pre-filled message
   * "OptIn:" + that code to the configured WhatsApp business number. The user sends the message to complete opt-in.
   * @returns The opt-in code that was opened in WhatsApp, or null if the API call failed or config is missing.
   */
  async startOptInAndOpenWhatsApp(): Promise<string | null> {
    await this.envConfig.EnsureNetworkConfiguration('WhatsApp opt-in');
    const whatsAppNumber = this.envConfig.getWhatsAppNumber();
    if (!whatsAppNumber) {
      return null;
    }

    const profile = await this.myProfileService.GetProfileStatic();
    const now = new Date();

    const body: OptInRequest = {
      currentTime: formatISO(now),
      dateOfBirth: profile?.dateOfBirth ?? null,
      gender: (profile?.gender ?? null) as OptInRequest['gender'],
    };

    const obs = this.consumerService.apiConsumerWhatsappOptinPost({ body });
    const response = await this.callApi(obs, this.NETWORK_TIMEOUT, 'WhatsApp opt-in');
    const code = response?.optInCode?.trim();
    if (!code) {
      return null;
    }
    const message = OPT_IN_PREFIX + code;
    await this.openWithPreFilledMessage(message, whatsAppNumber);
    await SecureStorageService.set(SecureStorageKey.WhatsAppOptedInPrompted, 'true');
    await SecureStorageService.remove(SecureStorageKey.WhatsAppFirtsPrompt);
    return code;
  }

  async ignoreWhatsAppOptInPrompt(): Promise<void> {
    await SecureStorageService.set(SecureStorageKey.WhatsAppOptedInPrompted, 'true');
    await SecureStorageService.remove(SecureStorageKey.WhatsAppFirtsPrompt);
  }

  /**
   * Resolves whether the health-score WhatsApp opt-in banner should show.
   * Records first-shown time on first eligibility; after 14+ calendar days acts like "Later"
   * (persists opted-in prompted, clears first-prompt key).
   */
  async evaluateWhatsAppOptInSection(): Promise<{ show: boolean; autoLaterMessage: boolean }> {
    const prompted = await SecureStorageService.get(SecureStorageKey.WhatsAppOptedInPrompted);
    if (prompted === 'true') {
      return { show: false, autoLaterMessage: false };
    }

    const firstRaw = await SecureStorageService.get(SecureStorageKey.WhatsAppFirtsPrompt);
    if (firstRaw) {
      const firstDate = parseISO(firstRaw);
      if (Number.isNaN(firstDate.getTime())) {
        await SecureStorageService.set(SecureStorageKey.WhatsAppFirtsPrompt, formatISO(new Date()));
      } else if (differenceInCalendarDays(new Date(), firstDate) >= WHATSAPP_OPT_IN_PROMPT_MAX_CALENDAR_DAYS) {
        await this.ignoreWhatsAppOptInPrompt();
        return { show: false, autoLaterMessage: true };
      }
    } else {
      await SecureStorageService.set(SecureStorageKey.WhatsAppFirtsPrompt, formatISO(new Date()));
    }

    return { show: true, autoLaterMessage: false };
  }

  /**
   * Strips non-digit characters for wa.me (international format, no + or spaces).
   * Returns empty string if nothing remains.
   */
  private normalizePhoneNumber(phone: string): string {
    return phone.replace(/\D/g, '');
  }
}
